/* @flow */

import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { observable, computed } from 'mobx';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { RouteComponentProps } from 'react-router-dom';
import Moment from 'react-moment';
import RequireForAccess from 'react-access';

//----------------------------------------------------------------------//
// MATERIAL UI                                                          //
//----------------------------------------------------------------------//

import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';

//----------------------------------------------------------------------//
//----------------------------------------------------------------------//

import styles from 'common-scss/DetailsPage.module.scss';
import StatusIndicator from 'components/StatusIndicator/StatusIndicator';
import ActionsList from 'components/ActionsList/ActionsList';
import PageTitle from 'components/PageTitleLegacy/PageTitle';
import PageWrapper from 'components/PageWrapper/PageWrapper';
import ActionDrawerWrapper from 'components/ActionDrawerWrapper/ActionDrawerWrapper';
import Loading from 'components/Loading/Loading';
import NavigationStore from 'stores/NavigationStore';
import ContractStore from 'stores/ContractStore';
import ActionDrawerStore from 'stores/ActionDrawerStore';
import { updateDownloadContractDocs } from 'permissions/contract';
import Activity from 'components/Activity/Activity';
import { DocumentList, ExpandedList } from 'components/DocumentList/DocumentList';
import ContractUsersRoleList from 'components/ContractUsersRoleList/ContractUsersRoleList';
import { ExtendContract } from 'views/ActionDrawer';
import { weeksAndDays } from 'utils/durations';
import * as statuses from 'utils/statuses';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import NavigationService from 'services/NavigationService';

import actionsListData from './actionsListData';
import CustomFields from 'components/CustomFields/CustomFields';
import { ElectroTooltip } from '../../components/ExtendedUiElements/customized';
import Documents from '../AssetDetailsPage/components/Documents/Documents';
import UserStore from 'stores/UserStore';
import { generateUrl, contract } from 'api/routes';

type TooltipArg = {
  label: string,
};

type InjectedProps = {
  contractStore: ContractStore,
  userStore: UserStore,
  actionDrawerStore: ActionDrawerStore,
  navigationStore: NavigationStore,
} & TranslatorProps &
  RouteComponentProps;

export const ContractSourceTypes = Object.freeze({
  PF: 'PF',
  ER: 'ER',
});

@translate()
@inject('contractStore', 'userStore', 'actionDrawerStore', 'navigationStore')
@observer
export default class Contract extends ComponentWithInjectedProps<{}, {}, InjectedProps> {
  @computed get isSale(): boolean {
    const {
      contractStore: { contract },
    } = this.props;

    return (
      !!contract &&
      typeof contract.order_type !== 'undefined' &&
      statuses.getCode(contract.order_type) === 'sale'
    );
  }

  scrollIntoView() {
    const {
      props: { match, location },
    } = this;

    if (location.hash === '#products') {
      const products = document.getElementById('products');

      if (products) {
        products.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }
    }
  }

  componentDidMount() {
    this.props.contractStore.selectedContractId = this.props.match.params.id;
    this.props.contractStore.loadContract(this.props.match.params.id);
    this.props.navigationStore.reset();
    this.props.navigationStore.pushPath(0, '/orders');
    this.props.navigationStore.pushPath(1, this.props.location.pathname);
    this.props.contractStore.loadDocuments(this.props.match.params.id);

    this.scrollIntoView();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { contractStore, match }: { contractStore: observable, match: Object } = this.props;

    if (contractStore.selectedContractId !== match.params.id) {
      contractStore.selectedContractId = match.params.id;
    }

    this.scrollIntoView();
  }

  componentWillUpdate(nextProps, nextState, nextContext) {
    this.scrollIntoView();
  }

  handleMyUploadId() {
    this.myUploadid.current.click();
  }

  renderRow = (label: string, value: *, hideOnNoValue = false) => {
    const { t } = this.props;
    if (hideOnNoValue && !value) {
      return null;
    }

    return (
      <tr key={`this.renderRow-${label}`}>
        <td>
          <b>{t(label)}:</b>
        </td>
        <td>{value}</td>
      </tr>
    );
  };

  componentWillUnmount() {
    this.props.actionDrawerStore.clearActionDrawer();
  }

  renderContractClosedDateRow() {
    const {
      contractStore: { contract },
    } = this.props;

    const contractStatusCode = statuses.getCode(contract.status);
    if (contractStatusCode === 'closed' && contract.er_end_date) {
      return this.dateRow('general:contracts.labels.closed_date', contract.er_end_date);
    }
  }

  addressRow(label: string, lines: Array<string>): React.Element<*> {
    const t: Function = this.props.t;

    return (
      <tr key={`this.addressRow-${label}`}>
        <td>
          <b>{t(label)}:</b>
        </td>
        <td>
          <address>
            <ul>
              {lines.map((l: string) => {
                if (!l) {
                  return null;
                }

                return <li key={l}>{l}</li>;
              })}
            </ul>
          </address>
        </td>
      </tr>
    );
  }

  dateRow(label: string, value: Date, tooltip?: TooltipArg): React.Element<*> {
    const t: Function = this.props.t;

    return (
      <tr key={`this.dateRow-${label}`}>
        {tooltip ? (
          <ElectroTooltip
            classes={{
              arrowPopper: 'contractStartDateTooltip',
            }}
            title={t(tooltip.label)}
          >
            <td>
              <b>{t(label)}:</b>
            </td>
          </ElectroTooltip>
        ) : (
          <td>
            <b>{t(label)}:</b>
          </td>
        )}
        <td>{value ? <Moment date={value} format="MMMM DD, YYYY" /> : null}</td>
      </tr>
    );
  }

  usersInRole(role: string): Array<*> {
    return this.props.contractStore.usersByRole[role] || [];
  }

  renderUsersAssigned() {
    return this.props.contractStore.loading ? (
      <Loading />
    ) : (
      this.props.contractStore.roles.map(
        (role: string, i: number) =>
          this.usersInRole(role).length && (
            <DocumentList
              key={role}
              collapsible={true}
              header={
                <React.Fragment>
                  {role} ({this.usersInRole(role).length})
                </React.Fragment>
              }
              listItems={this.usersInRole(role)}
            />
          )
      )
    );
  }

  renderProducts() {
    const t = this.props.t;
    const contract: * = this.props.contractStore.contract;

    const isLinkDisabled = (len: number) =>
      `${styles['layout-button']} ${len === 0 ? styles.disabled : ''}`;

    if (!contract.product.length) {
      return null;
    }

    // Sort products by line number
    contract.product.sort(
      (productA, productB) => productA.contract_line_no - productB.contract_line_no
    );

    const getAssetsLink = (contractId, modelId, assetStatuses = []) => {
      return NavigationService.formulateReportViewUri('/assets', {
        page: 1,
        filters: [
          { key: 'contract_id', values: [contractId] },
          { key: 'model', values: [modelId] },
          { key: 'asset_status', values: [...assetStatuses] },
        ],
      });
    };

    return (
      <React.Fragment>
        <h3 id={'products'}>{t('general:contracts.products')}</h3>
        <ExpandedList
          isExpanded={this.props.location.state && this.props.location.state.productsExpanded}
          header={<React.Fragment>{t('general:product_plural')}</React.Fragment>}
        >
          <table className={styles.productTable}>
            <tbody>
              <tr>
                <th style={{ width: '5%', textAlign: 'center' }}>
                  {t('general:labels.quantity')} ({contract.product_count})
                </th>
                <th style={{ width: '20%', textAlign: 'center' }}>
                  {t('general:products.labels.information')}
                </th>
                <th style={{ width: '5%', textAlign: 'center' }}>
                  {t('general:products.labels.rent_lease')}
                </th>
                <th style={{ width: '5%', textAlign: 'center' }}>
                  {t('general:products.labels.purchased')}
                </th>
                <th style={{ width: '5%', textAlign: 'center' }}>
                  {t('general:products.labels.returned')}
                </th>
              </tr>
              {contract.product.map((row, index) => (
                <React.Fragment>
                  <tr key={`contract-product-${row.product_id}`}>
                    <td className={styles['center']}>{row.products_count}</td>
                    <td>
                      {row.manufacturer}
                      <br />
                      {row.model}
                      <br />
                      {row.product_description}
                    </td>
                    <td className={styles['center']} style={{ width: '5%' }}>
                      {row.rented_assets_count > 0 ? (
                        <Link to={getAssetsLink(contract.id, row.model, ['On Lease', 'On Rent'])}>
                          {row.rented_assets_count}
                        </Link>
                      ) : (
                        row.rented_assets_count
                      )}
                    </td>
                    <td className={styles['center']}>
                      {row.sold_assets_count > 0 ? (
                        <Link to={getAssetsLink(contract.id, row.model, ['Sold'])}>
                          {row.sold_assets_count}
                        </Link>
                      ) : (
                        row.sold_assets_count
                      )}
                    </td>
                    <td className={styles['center']}>
                      {row.returned_assets_count > 0 ? (
                        <Link to={getAssetsLink(contract.id, row.model, ['Returned'])}>
                          {row.returned_assets_count}
                        </Link>
                      ) : (
                        row.returned_assets_count
                      )}
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </ExpandedList>
      </React.Fragment>
    );
  }

  renderDeliveryNote() {
    const { t } = this.props;

    return (
      <React.Fragment>
        <h3>{t('general:contracts.delivery_note_header')}</h3>
        <p>{t('general:contracts.delivery_note')}</p>
      </React.Fragment>
    );
  }

  renderAssignedToContract() {
    const {
      t,
      contractStore: { contract },
    } = this.props;

    const assetsLink = NavigationService.formulateReportViewUri('/assets', {
      page: 1,
      filters: [
        { key: 'contract_id', values: [contract.id] },
        { key: 'asset_status', values: ['On Rent', 'Sold'] },
      ],
    });

    return (
      <React.Fragment>
        <h3>{t('general:assets.messages.configured_to_contract')}</h3>
        <table className="layout-table large-first-col">
          <tbody>
            <tr>
              <td style={{ width: '130px' }}>{t('general:asset_plural')}</td>
              <td className="layout-flex">
                <div>
                  <span>{contract.assets_count}</span>
                  {contract.assets_count === 0 ? (
                    <Button
                      color="primary"
                      variant="contained"
                      className="layout-button"
                      disabled={contract.assets_count === 0}
                    >
                      {t('general:actions.view_asset', { count: contract.assets_count })}
                    </Button>
                  ) : (
                    <Link to={assetsLink}>
                      <Button color="primary" variant="contained" className="layout-button">
                        {t('general:actions.view_asset', { count: contract.assets_count })}
                      </Button>
                    </Link>
                  )}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </React.Fragment>
    );
  }

  renderActivity() {
    return (
      <Paper className={styles['activity-panel']}>
        {this.props.match.params.id && (
          <Activity
            title={true}
            url={generateUrl(contract.activity, [this.props.match.params.id])}
            refresh={this.props.actionDrawerStore.success}
            allowFilter={true}
          />
        )}
      </Paper>
    );
  }

  renderPage() {
    const {
      t,
      i18n,
      contractStore: { contract },
    } = this.props;

    const invoiceAddress = [...Array(5)].map((u, i) => {
      return contract.billing_details['invoice_address' + i];
    });

    const deliveryAddress = [...Array(5)].map(
      (u, i) => contract.delivery_details['delivery_address' + i]
    );

    const rows: React.Node = [
      this.renderRow('general:trading_accounts.labels.name', contract.account_name),
      this.renderRow('general:trading_accounts.labels.number', contract.account_no),
      this.renderRow('general:labels.company_name', contract.company_name),
      !!contract.quotation_number &&
        this.renderRow('general:labels.quotation_number', contract.quotation_number),
      this.renderRow('general:contracts.labels.number', contract.contract_number),
      this.renderRow('general:contracts.labels.type', contract.order_type),
      this.renderRow('general:contracts.labels.status', statuses.translate(contract.status, i18n)),
      this.renderRow(
        'general:contracts.labels.customer_document_number',
        contract.customer_document_number,
        true
      ),
      this.dateRow('general:labels.order_date', contract.er_contract_date),
      !this.isSale
        ? this.dateRow('general:contracts.labels.start_date', contract.er_start_date, {
            label: 'general:contracts.labels.first_shipment_date',
          })
        : this.dateRow('general:assets.purchased_date', contract.er_start_date),
      !this.isSale &&
        this.dateRow('general:contracts.labels.expected_end_date', contract.er_expected_end_date),
      !this.isSale &&
        this.renderRow(
          'general:contracts.labels.duration',
          <span>
            {contract.er_expected_end_date && contract.er_start_date
              ? weeksAndDays(contract.er_start_date, contract.er_expected_end_date, t)
              : null}
          </span>
        ),

      this.renderRow('general:contracts.labels.contact', contract.contact_name),
      this.renderRow('general:contracts.labels.email', contract.contact_email),
      this.renderContractClosedDateRow(),
    ].filter(row => row);

    return (
      <React.Fragment>
        <section>
          <h3>{t('general:contracts.labels.details')}</h3>
          <table>
            <tbody>{rows}</tbody>
          </table>
        </section>

        {contract.custom_fields && !!contract.custom_fields.length && (
          <CustomFields
            title={t('general:assets.labels.custom_fields')}
            type={'contract'}
            customFields={contract.custom_fields}
          />
        )}

        <Grid className={styles['grid-layout']} container={true}>
          <Grid item={true} xs={12} sm={6}>
            <h3>{t('general:labels.billing_details')}</h3>
            <table>
              <tbody>
                {this.renderRow('general:labels.po_number', contract.billing_details.po_number)}
                {contract.billing_details.cover_po_start_date &&
                  this.dateRow(
                    'general:contracts.labels.cover_po_start_date',
                    contract.billing_details.cover_po_start_date
                  )}
                {contract.billing_details.rental_po_expiration_date &&
                  this.dateRow(
                    'general:contracts.labels.cover_po_end_date',
                    contract.billing_details.rental_po_expiration_date
                  )}
                {this.addressRow(
                  'general:labels.invoice_address',
                  invoiceAddress.concat([contract.billing_details.invoice_postcode])
                )}
              </tbody>
            </table>
          </Grid>

          <Grid item={true} xs={12} sm={6}>
            <h3>{t('general:labels.delivery_pickup_details')}</h3>
            <table>
              <tbody>
                {this.addressRow(
                  'general:labels.delivery_address',
                  deliveryAddress.concat([contract.delivery_details.delivery_postcode])
                )}
                {this.renderRow(
                  'general:labels.delivery_address_info',
                  contract.delivery_details.delivery_info,
                  true
                )}
                {this.renderRow('general:assets.labels.quantity', contract.assets_count)}
                {this.renderRow(
                  'general:assets.labels.your_reference',
                  contract.billing_details.po_number
                )}
                {contract.er_consignee_name &&
                  this.renderRow(
                    'general:assets.labels.consignee_name',
                    contract.er_consignee_name
                  )}
                {contract.er_consignee_email &&
                  this.renderRow(
                    'general:assets.labels.consignee_email',
                    contract.er_consignee_email
                  )}
                {contract.er_consignee_telephone &&
                  this.renderRow(
                    'general:assets.labels.consignee_telephone',
                    contract.er_consignee_telephone
                  )}
              </tbody>
            </table>
          </Grid>
        </Grid>

        <section>{this.renderProducts()}</section>
        <section>
          <ContractUsersRoleList
            details={contract}
            contractId={contract.id}
            accountId={contract.account_id}
          />
        </section>
        {contract.assets_count > 0 && (
          <RequireForAccess permissions={updateDownloadContractDocs}>
            <section>{this.renderDeliveryNote()}</section>

            <Documents title={t('general:documents')} documentType={'contract'} />
          </RequireForAccess>
        )}

        {contract.source === ContractSourceTypes.PF && (
          <Documents
            title={t('general:contracts.certificate_of_conformance')}
            documentType={'contract_coc'}
          />
        )}
      </React.Fragment>
    );
  }

  isActionsAllowed(status: string): number {
    const allowedStatuses = ['open'];
    return allowedStatuses.indexOf(status);
  }

  render() {
    const { t, actionDrawerStore, contractStore } = this.props;

    if (!contractStore.contract) {
      return (
        <PageWrapper>
          <Loading />
        </PageWrapper>
      );
    }

    const contract: * = this.props.contractStore.contract;

    const title: string = `${t('general:contract')} | ${contract.contract_number}`;

    return (
      <React.Fragment>
        <PageWrapper isFlex={false}>
          <PageTitle title={title} />
          <div className={styles['grid-wrapper']}>
            <Paper className={styles['feature-item']}>
              <div className={styles['details-panel']}>{this.renderPage()}</div>
              <div className={styles['actions-panel']}>
                <StatusIndicator
                  title={t('general:contracts.labels.status')}
                  status={contract.status}
                />
                {this.isActionsAllowed(contract.status.toLowerCase()) >= 0 && (
                  <ActionsList actions={actionsListData} data={contract} />
                )}
              </div>
            </Paper>
            {this.renderActivity()}
          </div>
        </PageWrapper>

        <ActionDrawerWrapper
          appendActionToURL={true}
          onActionCompleted={() => {
            /*overriding with empty fn*/
          }}
        >
          {
            {
              extendContract: <ExtendContract contract={contract} />,
            }[actionDrawerStore.currentActionDrawer]
          }
        </ActionDrawerWrapper>
      </React.Fragment>
    );
  }
}
